import React, { useEffect, useState } from "react";
import { Bell, X, Trophy, Flame, GraduationCap, Compass, Sparkles, CreditCard, CheckCheck, Loader2 } from "lucide-react";
import client from "@/lib/api";
import { useLang } from "@/context/LangContext";

const ICON = {
  achievement: { Icon: Trophy, tint: "#F59E0B" },
  streak: { Icon: Flame, tint: "#EF4444" },
  reminder: { Icon: Compass, tint: "#22C55E" },
  progress: { Icon: GraduationCap, tint: "#2563EB" },
  content: { Icon: Sparkles, tint: "#8B5CF6" },
  pro: { Icon: CreditCard, tint: "#F59E0B" },
  welcome: { Icon: Sparkles, tint: "#2563EB" },
};

const ago = (iso, t) => {
  try {
    const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return t("time_just_now");
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h`;
    if (s < 604800) return `${Math.floor(s / 86400)}d`;
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return ""; }
};

export default function NotificationCenter({ open, onClose, onChange }) {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const titleFor = (n) => {
    if (n.type === "achievement") return t("notif_ach_title");
    if (n.type === "welcome") return t("notif_welcome_title");
    return n.title;
  };
  const msgFor = (n) => (n.type === "welcome" ? t("notif_welcome_msg") : n.message);

  const load = () => {
    setLoading(true);
    client.get("/notifications").then((r) => setItems(r.data?.items || [])).catch(() => setItems([])).finally(() => setLoading(false));
  };
  useEffect(() => { if (open) load(); }, [open]);

  const markAll = async () => {
    setItems((xs) => xs.map((x) => ({ ...x, read: true })));
    try { await client.post("/notifications/read-all"); onChange?.(); } catch {}
  };
  const markOne = async (n) => {
    if (n.read) return;
    setItems((xs) => xs.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    try { await client.post(`/notifications/${n.id}/read`); onChange?.(); } catch {}
  };
  const unread = items.filter((x) => !x.read).length;

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex justify-center" onClick={onClose} data-testid="notif-center">
      <div className="w-full max-w-[460px] h-full bg-[#F8FAFC] flex flex-col" onClick={(e) => e.stopPropagation()}
        style={{ paddingTop: "env(safe-area-inset-top,0px)" }}>
        <div className="flex items-center justify-between px-5 pt-11 pb-3 border-b border-slate-200 bg-white">
          <div className="flex items-center gap-2">
            <Bell size={20} className="text-[#2563EB]" />
            <h1 className="font-heading text-xl font-extrabold text-[#0F172A]">{t("notif_title")}</h1>
            {unread > 0 && <span className="text-[11px] font-bold rounded-full px-2 py-0.5 bg-[#2563EB] text-white">{unread}</span>}
          </div>
          <button onClick={onClose} data-testid="notif-close" className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center"><X size={18} className="text-[#0F172A]" /></button>
        </div>

        {unread > 0 && (
          <button onClick={markAll} data-testid="notif-mark-all" className="self-end mx-5 mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#2563EB]"><CheckCheck size={16} /> {t("notif_mark_all")}</button>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-4" style={{ paddingBottom: "calc(env(safe-area-inset-bottom,0px) + 24px)" }}>
          {loading ? <div className="flex justify-center pt-16"><Loader2 className="animate-spin text-[#2563EB]" /></div>
            : items.length === 0 ? (
              <div className="text-center pt-20 text-slate-400">
                <Bell size={40} className="mx-auto mb-3 opacity-40" />
                <p className="font-semibold text-[#0F172A]">{t("notif_caught_up")}</p>
                <p className="text-sm mt-1">{t("notif_empty_sub")}</p>
              </div>
            ) : (
              <div className="space-y-2.5">
                {items.map((n) => {
                  const { Icon, tint } = ICON[n.type] || ICON.welcome;
                  return (
                    <button key={n.id} onClick={() => markOne(n)} data-testid={`notif-item-${n.id}`}
                      className={`w-full text-left rounded-2xl p-3.5 flex gap-3 items-start border transition-colors ${n.read ? "bg-white border-slate-200" : "bg-[#EAF1FE] border-[#2563EB]/20"}`}>
                      <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${tint}18`, color: tint }}><Icon size={18} /></span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-bold text-[#0F172A] text-sm">{titleFor(n)}</p>
                          {!n.read && <span className="w-2 h-2 rounded-full bg-[#2563EB] shrink-0" />}
                        </div>
                        <p className="text-slate-600 text-sm mt-0.5">{msgFor(n)}</p>
                        <p className="text-slate-400 text-xs mt-1">{ago(n.created_at, t)}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
        </div>
      </div>
    </div>
  );
}
